import flagsmith from 'flagsmith';

import { SubscriptionTier } from '@invoice-simple/common';

import UserModel, { Cadence } from 'src/models/UserModel';
import { getStripeCheckoutSession } from './stripeCheckout';

// Bookkeeping prices in cents (USD base prices)
const BOOKKEEPING_PRICES = {
  monthly: 2000, // $20.00
  annual: 20000 // $200.00 ($20/mo × 10 months)
};

// Bookkeeping bundle prices in cents (tier + bookkeeping)
const BOOKKEEPING_BUNDLE_PRICES = {
  [SubscriptionTier.ESSENTIALS]: { monthly: 2699, annual: 26999 },
  [SubscriptionTier.PLUS]: { monthly: 3499, annual: 34999 },
  [SubscriptionTier.PREMIUM]: { monthly: 4199, annual: 41999 }
} as const;

export type BookkeepingTier =
  | SubscriptionTier.ESSENTIALS
  | SubscriptionTier.PLUS
  | SubscriptionTier.PREMIUM;

export const getBookkeepingBundlePrice = (
  tier: BookkeepingTier,
  cadence: Cadence,
  currencyCode = 'USD',
  tierPriceCents?: number
): number => {
  if (tierPriceCents !== undefined) {
    return tierPriceCents + getBookkeepingPrice(cadence, currencyCode);
  }

  return BOOKKEEPING_BUNDLE_PRICES[tier][cadence];
};

// Localized bookkeeping prices in cents (English-speaking locales only)
const LOCALIZED_BOOKKEEPING_PRICES: Partial<Record<string, { monthly: number; annual: number }>> = {
  CAD: { monthly: 2800, annual: 28000 },
  AUD: { monthly: 3000, annual: 30000 },
  GBP: { monthly: 1600, annual: 16000 },
  NZD: { monthly: 3300, annual: 33000 }
};

const LOCALIZED_PRICE_CURRENCIES = ['CAD', 'AUD', 'GBP', 'NZD'];

export const getBookkeepingPrice = (cadence: Cadence, currencyCode = 'USD'): number => {
  if (LOCALIZED_PRICE_CURRENCIES.includes(currencyCode)) {
    const localizedPrices = LOCALIZED_BOOKKEEPING_PRICES[currencyCode];
    if (localizedPrices) {
      return localizedPrices[cadence];
    }
  }
  return BOOKKEEPING_PRICES[cadence];
};

export async function isStripePurchaseTransferrable(sessionId: string): Promise<boolean> {
  if (!sessionId) {
    return false;
  }

  const user = UserModel.getInstance();
  const checkoutSession = await getStripeCheckoutSession(sessionId);
  const accountIdMatches = checkoutSession?.metadata?.accountId === user.accountId;
  const toBeTransferred = !!checkoutSession?.metadata?.toBeTransferred;
  return accountIdMatches && toBeTransferred;
}

export function shouldOfferLocalizedPrices() {
  const localizedPricesEnabled = false; // Feature flag to enable localized prices

  if (!localizedPricesEnabled) {
    return false;
  }
  const user = UserModel.getInstance();
  const userCurrencyCode = user.countrySettings.subscription.currencyCode;
  const shouldEnableABTest = false;

  if (!shouldEnableABTest) {
    const localizedPriceCurrencies = ['CAD', 'AUD', 'INR', 'GBP'];
    return localizedPriceCurrencies.includes(userCurrencyCode);
  }

  const hasFeature = flagsmith.hasFeature('localized_prices');

  if (hasFeature && user.isSubPaid) {
    if (user.lastPaidSub?.orderSku.includes('local')) return true;
  }

  return hasFeature && !user.isSubPaid;
}
